/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactElement, type SVGProps, cloneElement, createContext, forwardRef, isValidElement } from 'react';
import { useHover } from 'react-aria';
import { type ContextValue, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'style' | 'className'> {
  id?: string;
  slot?: string;
  /** The size of the icon in pixels. (Default: 24) */
  size?: number | { height: number, width: number };
  /** The color variant for the icon. (Default: 'primary') */
  color?: keyof typeof strokeVariants;
  /** Whether the icon should be colored. If true, the color prop will be ignored. (Default: false) */
  isColored?: boolean;
  /** Whether the icon should use filled styling instead of stroke. (Default: false) */
  isFilled?: boolean;
  style?: stylex.StyleXStyles;
  children?: ReactElement<SVGProps<SVGSVGElement>>;
}

const IconContext = createContext<ContextValue<IconProps, SVGSVGElement>>({});

const Icon = forwardRef((props: IconProps, ref: ForwardedRef<SVGSVGElement>) => {
  [props, ref] = useContextProps(props, ref, IconContext);

  const { size = 24, isFilled = false, color = 'primary', isColored = false, style, children, ...rest } = props;

  const { hoverProps } = useHover({});

  if (!props['aria-label'] && !props['aria-labelledby']) {
    rest['aria-hidden'] = 'true';
  }


  if (!isValidElement(children)) {
    throw new Error('Icon must have a valid child element');
  }

  return cloneElement(children, {
    className: stylex.props(
      styles.base,
      !isColored && (isFilled ? fillVariants[color as keyof typeof fillVariants] : strokeVariants[color]),
      style
    ).className,
    style: {
      height: typeof size === 'number' ? `${size}px` : `${size.height}px`,
      minHeight: typeof size === 'number' ? `${size}px` : `${size.height}px`,
      minWidth: typeof size === 'number' ? `${size}px` : `${size.width}px`,
      width: typeof size === 'number' ? `${size}px` : `${size.width}px`,
    },
    ...rest,
    ...hoverProps,
    ref
  });
});

const styles = stylex.create({
  base: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'inline-flex',
    justifyContent: 'center',
  },
});

const strokeVariants = stylex.create({
  primary: { stroke: $.iconPrimary },
  secondary: { stroke: $.iconSecondary },
  tertiary: { stroke: $.iconTertiary },
  quaternary: { stroke: $.iconQuaternary },
  disabled: { stroke: $.iconDisabled },
  inverted: { stroke: $.iconInverted },
  interactive: { stroke: $.iconInteractive },
  positive: { stroke: $.iconPositive },
  attentive: { stroke: $.iconAttentive },
  destructive: { stroke: $.iconDestructive },
  white: { stroke: $.iconWhite },
  black: { stroke: $.iconBlack },
  // ! The following colors have been added because the icon uses them (ask design team to standardize colors)
  critical: { stroke: $.colorRed50 },
  yellow: { stroke: $.accentSun80 },
  orange: { stroke: $.accentPumpkin80 },
  turquoise: { stroke: $.accentTurquoise80 },
  blueLight: { stroke: $.accentOcean80 },
  blueDark: { stroke: $.accentSky80 },
  violet: { stroke: $.accentPlum60 },
  // ! The following colors have been added because the button uses them (ask design team to standardize colors)
  primaryHover: { stroke: $.buttonContentPrimaryHover },
  tertiaryHover: { stroke: $.buttonContentTertiaryHover },
  // ! The following colors have been added because the checkbox uses them (ask design team to standardize colors)
  criticalSilent: { stroke: $.surfaceCriticalSilent },
  // ! The following colors have been added because the snackbar uses them (ask design team to standardize colors)
  cucumber80: { stroke: $.accentCucumber80 },
  tertiaryDark: { stroke: '#C8CFFF' },
  inherit: { stroke: 'inherit' },
  buttonContentDisabled: { stroke: $.buttonContentDisabled },
});

const fillVariants = stylex.create({
  primary: { fill: $.iconPrimary },
  secondary: { fill: $.iconSecondary },
  tertiary: { fill: $.iconTertiary },
  quaternary: { fill: $.iconQuaternary },
  disabled: { fill: $.iconDisabled },
  inverted: { fill: $.iconInverted },
  interactive: { fill: $.iconInteractive },
  positive: { fill: $.iconPositive },
  attentive: { fill: $.iconAttentive },
  destructive: { fill: $.iconDestructive },
  white: { fill: $.iconWhite },
  black: { fill: $.iconBlack },
  // ! The following colors have been added because the icon uses them (ask design team to standardize colors)
  critical: { fill: $.colorRed50 },
  yellow: { fill: $.accentSun80 },
  orange: { fill: $.accentPumpkin80 },
  turquoise: { fill: $.accentTurquoise80 },
  blueLight: { fill: $.accentOcean80 },
  blueDark: { fill: $.accentSky80 },
  violet: { fill: $.accentPlum60 },
  // ! The following colors have been added because the button uses them (ask design team to standardize colors)
  primaryHover: { fill: $.buttonContentPrimaryHover },
  tertiaryHover: { fill: $.buttonContentTertiaryHover },
  // ! The following colors have been added because the snackbar uses them (ask design team to standardize colors)
  cucumber80: { fill: $.accentCucumber80 },
  tertiaryDark: { stroke: '#C8CFFF' },
  inherit: { stroke: 'inherit' },
  buttonContentDisabled: { fill: $.buttonContentDisabled },
});

export { Icon, IconContext, type IconProps };
